import type RootStore from "stores/RootStore";
import type { RouteType } from "types/RouteType";
import type InvoicesStore from "./InvoicesStore";

/**
 * A failed save THROWS: the dialog framework keeps a dialog open when its
 * onSubmit rejects and shows the message in the footer, so the API's error
 * stays visible with the form intact. Resolving closes it.
 */
const saveOrThrow = async (rootStore: RootStore) => {
  const store = (rootStore as unknown as { invoicesViewStore?: InvoicesStore }).invoicesViewStore;
  if (!(await store?.save())) {
    throw new Error(store?.formError || "Failed to save the invoice");
  }
};

const submitValidation = ({ rootStore }: { rootStore: RootStore }) =>
  ((rootStore as unknown as { invoicesViewStore?: InvoicesStore }).invoicesViewStore?.isFormValid) === true;

/**
 * Billing → Invoices. Gated by gateBillingRoutes (see ../billingGate.ts): this
 * route does not appear, and cannot be reached by URL, on an instance without
 * the billing env keys.
 *
 * `requiresAdmin` on top of that: this page views, creates and marks paid the
 * company's invoices, so it is at least as sensitive as the pages that already
 * demand admin (LLM providers, user management). The API's finance-role RBAC is
 * still the authority — this only stops the nav from offering a page whose
 * writes the server would refuse.
 */
const invoicesRoute: RouteType[] = [
  {
    name: "Invoices",
    path: "/billing/invoices",
    layout: "/app",
    viewportCard: {
      header: {
        title: "Invoices",
        component: () => import("./Invoices.Header"),
        enableRefreshButton: true,
        enableBackButton: true,
      },
      body: { component: () => import("./Invoices.Body") },
    },
    group: "Billing",
    hidden: false,
    requiresAuth: true,
    requiresAdmin: true,
    requiresPermission: false,
    enableAutoFind: false,
    channels: ["invoices"],
    onEnter: async ({ rootStore }) => {
      const store = (rootStore as unknown as { invoicesViewStore?: InvoicesStore }).invoicesViewStore;
      void store?.initialize();
    },
    dialogs: [
      {
        name: "InvoiceFormDialog",
        title: "New Invoice",
        component: () => import("./dialogs/InvoiceFormDialog.Body"),
        cancelButton: true,
        maxWidth: "md",
        // The framework applies `height` as a FIXED px height on the dialog
        // content (Dialog.Body: `sx={{ height }}`) — there is no maxHeight /
        // viewport-relative option in DialogType, so the number itself has to
        // fit the smallest window we expect. 720 did not: a 768px-tall laptop
        // window leaves ~640px of paper after MUI's 64px inset, so the form ran
        // off the bottom with the submit row unreachable. 560 is the tallest
        // value that still fits that window with the dialog header and footer,
        // and the line-item list scrolls inside it. Phones are unaffected either
        // way — the router puts routed dialogs fullScreen there.
        height: 560,
        submitButtonText: "Create Invoice",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
          saveOrThrow(rootStore),
        onSubmitValidation: submitValidation,
      },
      {
        // Same body; the framework's title and submit label are static strings,
        // so create and edit are registered separately.
        name: "InvoiceEditDialog",
        title: "Edit Invoice",
        component: () => import("./dialogs/InvoiceFormDialog.Body"),
        cancelButton: true,
        maxWidth: "md",
        // Same body, so the same height — see the create dialog above.
        height: 560,
        submitButtonText: "Save Changes",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
          saveOrThrow(rootStore),
        onSubmitValidation: submitValidation,
      },
    ],
  },
];

export default invoicesRoute;
